import { useEffect, useRef, type MutableRefObject } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Socket } from 'socket.io-client';
import { useTenantStore } from '@/store/tenant.store';
import { usePersonnelTrackingStore } from '@/store/personnel-tracking.store';
import { api } from '@/services/api';
import { sendCheckIn, sendSos } from '@/services/field-ingest.service';
import { safetyOutboxService } from '@/offline/safety-outbox.service';
import { toast } from '@/utils/toast';
import { useTenantQuery } from './useTenantQuery';
import { parseApiError } from '@/utils/api-errors';
import { claimPersonnelRoom } from './personnelRoomClaims';
import type { ZoneAlertingState } from '@/constants/honest-ui';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PersonnelSession {
  id: string;
  tenant_id: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  incident_id?: string | null;
  device_id?: string | null;
  started_at: string;
  ended_at?: string | null;
  // Mirrors the DB CHECK constraint (migration 121_personnel_tracking.sql) and
  // the backend Zod enum. 'ended' was never a real status — endSession() writes
  // 'offline' — and its presence here let the UI ship a status filter value the
  // backend 400s on. Keep this union in lockstep with the backend enum.
  status: 'active' | 'paused' | 'offline' | 'emergency';
  role?: string | null;
  callsign?: string | null;
  // Unified incident-org tree assignment (incident_org_nodes)
  org_node_id?: string | null;
  // Joined display field from incident_org_nodes (read queries only)
  org_node_name?: string | null;
  track_type?: string | null;
  last_lat?: number | null;
  last_lng?: number | null;
  last_battery_percent?: number | null;
  last_speed?: number | null;
  last_heading?: number | null;
  last_position_at?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface PersonnelPosition {
  id: string;
  session_id: string;
  user_id: string;
  user_name?: string;
  lat: number;
  lng: number;
  altitude?: number | null;
  accuracy?: number | null;
  speed?: number | null;
  heading?: number | null;
  battery_percent?: number | null;
  recorded_at: string;
  status?: string;
  role?: string | null;
  callsign?: string | null;
  track_type?: string | null;
}

export interface PersonnelCheckIn {
  id: string;
  tenant_id: string;
  user_id: string;
  user_name?: string;
  incident_id?: string | null;
  session_id?: string | null;
  check_in_type: 'routine' | 'arrival' | 'departure' | 'status_change' | 'hazard' | 'manual';
  lat?: number | null;
  lng?: number | null;
  message?: string | null;
  status: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  /** P2-04 field time: effective time (device time bounded by the server's skew rules). */
  occurred_at?: string;
  /** The device clock as sent; null for console actions. */
  device_occurred_at?: string | null;
  received_at?: string;
  /** The device clock was outside the accepted bounds. */
  clock_suspect?: boolean;
}

export interface SafetyZone {
  id: string;
  tenant_id: string;
  incident_id?: string | null;
  name: string;
  zone_type: 'exclusion' | 'safe_area' | 'staging' | 'evacuation' | 'hazard';
  geometry_geojson?: Record<string, unknown> | null;
  proximity_threshold_meters?: number | null;
  is_active: boolean;
  created_by?: string | null;
  created_by_name?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  /** Per-zone alert policy (P1-09, migration 543). */
  zone_policy?: SafetyZonePolicy;
  /** What the zone does right now, computed by the API (P1-09). */
  alerting_state?: ZoneAlertingState;
  /** The server could read the zone's shape (an alert policy needs it). */
  geometry_readable?: boolean;
  /** 'geofence': a copy kept in step with a geofence (edit its shape there). */
  source_type?: string | null;
  alert_on_proximity?: boolean;
  hysteresis_meters?: number;
}

export type SafetyZonePolicy = 'reference' | 'monitor' | 'no_entry';

export interface SafetyZoneAlert {
  id: string;
  safety_zone_id: string;
  zone_name?: string;
  session_id: string;
  user_id: string;
  user_name?: string;
  alert_type: 'entered' | 'exited' | 'proximity' | 'breach';
  lat: number;
  lng: number;
  distance_meters?: number | null;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface PersonnelSessionFilters {
  incident_id?: string;
  status?: string;
  callsign?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PersonnelCheckInFilters {
  incident_id?: string;
  session_id?: string;
  check_in_type?: string;
  user_id?: string;
  page?: number;
  limit?: number;
}

export interface SafetyZoneFilters {
  incident_id?: string;
  zone_type?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
}

export interface SafetyZoneAlertFilters {
  safety_zone_id?: string;
  alert_type?: string;
  acknowledged?: boolean;
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// Query keys factory
// ---------------------------------------------------------------------------

const KEYS = {
  all: ['personnel-tracking'] as const,
  sessions: (filters?: PersonnelSessionFilters) =>
    [...KEYS.all, 'sessions', filters] as const,
  session: (id: string) => [...KEYS.all, 'sessions', 'item', id] as const,
  latestPositions: (incidentId?: string) =>
    [...KEYS.all, 'positions', 'latest', incidentId] as const,
  trail: (sessionId: string) =>
    [...KEYS.all, 'sessions', sessionId, 'trail'] as const,
  checkIns: (filters?: PersonnelCheckInFilters) =>
    [...KEYS.all, 'check-ins', filters] as const,
  safetyZones: (filters?: SafetyZoneFilters) =>
    [...KEYS.all, 'safety-zones', filters] as const,
  safetyZone: (id: string) =>
    [...KEYS.all, 'safety-zones', 'item', id] as const,
  safetyZoneAlerts: (filters?: SafetyZoneAlertFilters) =>
    [...KEYS.all, 'safety-zones', 'alerts', filters] as const,
  /** Sessions in emergency status — the app-shell SOS alarm's polling fallback. */
  openSos: () => [...KEYS.all, 'sos', 'open'] as const,
};

export { KEYS as personnelTrackingKeys };

// ---------------------------------------------------------------------------
// Query hooks (GET requests using useTenantQuery)
// ---------------------------------------------------------------------------

/**
 * Fetch paginated personnel tracking sessions
 */
export function usePersonnelSessions(filters?: PersonnelSessionFilters) {
  return useTenantQuery<PaginatedResponse<PersonnelSession>>(
    KEYS.sessions(filters),
    '/personnel-tracking/sessions',
    {
      params: filters,
      staleTime: 10000, // 10s - sessions update frequently
    }
  );
}

/**
 * Fetch latest positions for all active personnel
 */
export function useLatestPositions(incidentId?: string) {
  return useTenantQuery<PersonnelPosition[]>(
    KEYS.latestPositions(incidentId),
    '/personnel-tracking/positions/latest',
    {
      params: incidentId ? { incident_id: incidentId } : undefined,
      staleTime: 5000, // 5s - positions are near-real-time
    }
  );
}

/**
 * Fetch breadcrumb trail for a specific session
 */
export function usePersonnelTrail(sessionId: string) {
  return useTenantQuery<PersonnelPosition[]>(
    KEYS.trail(sessionId),
    `/personnel-tracking/sessions/${sessionId}/trail`,
    {
      enabled: !!sessionId,
      staleTime: 15000,
    }
  );
}

/**
 * Fetch personnel check-ins
 */
export function usePersonnelCheckIns(filters?: PersonnelCheckInFilters) {
  return useTenantQuery<PaginatedResponse<PersonnelCheckIn>>(
    KEYS.checkIns(filters),
    '/personnel-tracking/check-ins',
    {
      params: filters,
      staleTime: 15000,
    }
  );
}

/**
 * Fetch safety zones
 */
export function useSafetyZones(filters?: SafetyZoneFilters) {
  return useTenantQuery<PaginatedResponse<SafetyZone>>(
    KEYS.safetyZones(filters),
    '/personnel-tracking/safety-zones',
    {
      params: filters,
      staleTime: 30000,
    }
  );
}

/**
 * Fetch safety zone alerts
 */
export function useSafetyZoneAlerts(filters?: SafetyZoneAlertFilters) {
  return useTenantQuery<PaginatedResponse<SafetyZoneAlert>>(
    KEYS.safetyZoneAlerts(filters),
    '/personnel-tracking/safety-zones/alerts',
    {
      params: filters,
      staleTime: 10000,
    }
  );
}

// ---------------------------------------------------------------------------
// Mutation hooks (POST / PATCH / DELETE using api client)
// ---------------------------------------------------------------------------

/**
 * Start a new tracking session
 */
export function useStartSession() {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantStore();
  const store = usePersonnelTrackingStore();

  return useMutation({
    mutationFn: async (data: {
      incident_id?: string;
      team_name?: string;
      assigned_role?: string;
      // Unified org tree assignment (incident_org_nodes)
      org_node_id?: string;
      track_type?: string;
      device_model?: string;
      device_os?: string;
    }) => {
      return api.post<PersonnelSession>('/personnel-tracking/sessions/start', data);
    },
    onSuccess: (session) => {
      store.setCurrentSessionId(session.id);
      queryClient.invalidateQueries({ queryKey: [activeTenant?.id, ...KEYS.all] });
      toast.success('Tracking session started');
    },
    onError: (err: Error) => {
      toast.error('Failed to start tracking session', parseApiError(err).message);
    },
  });
}

/**
 * End an active tracking session
 */
export function useEndSession() {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantStore();
  const store = usePersonnelTrackingStore();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      return api.post<PersonnelSession>(
        `/personnel-tracking/sessions/${sessionId}/end`,
        {}
      );
    },
    onSuccess: () => {
      // Only clear tracking state -- do NOT call reset() / stopGeolocationWatch()
      // because the caller already awaited stopGeolocationWatch() before mutating.
      // Calling reset() here would trigger a second stopGeolocationWatch(), causing
      // a double-stop race condition.
      store.setCurrentSessionId(null);
      store.setCallsign(null);
      store.setTrackType(null);
      store.setStartedAt(null);
      usePersonnelTrackingStore.setState({
        isTracking: false,
        pointCount: 0,
        lastAccuracy: null,
        error: null,
      });
      queryClient.invalidateQueries({ queryKey: [activeTenant?.id, ...KEYS.all] });
      toast.success('Tracking session ended');
    },
    onError: (err: Error) => {
      toast.error('Failed to end tracking session', parseApiError(err).message);
    },
  });
}

/**
 * Record one or more GPS positions (batch upload from buffer)
 */
export function useRecordPositions() {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantStore();

  return useMutation({
    mutationFn: async (data: {
      session_id: string;
      positions: Array<{
        lat: number;
        lng: number;
        altitude?: number;
        accuracy?: number;
        speed?: number;
        heading?: number;
        battery_percent?: number;
        recorded_at: string;
      }>;
    }) => {
      return api.post<{ recorded: number }>('/personnel-tracking/positions', data);
    },
    onSuccess: () => {
      // Silently invalidate position queries -- no toast for frequent GPS updates
      queryClient.invalidateQueries({
        queryKey: [activeTenant?.id, ...KEYS.all, 'positions'],
      });
    },
    onError: () => {
      // Silent failure for position recording -- will retry in next buffer flush
    },
  });
}

/**
 * Create a check-in
 */
export function useCreateCheckIn() {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantStore();

  return useMutation({
    mutationFn: async (data: {
      incident_id?: string;
      session_id?: string;
      check_in_type: string;
      lat?: number;
      lng?: number;
      message?: string;
      status?: string;
    }) => {
      // Lane-2 field endpoint with an event id (P1-12); legacy route for the
      // check-in types it does not take, or behind VITE_FIELD_INGEST_LEGACY.
      // The safety outbox keeps it until the server stores it (S-06 drain, §6.2 step 2(a)).
      return sendCheckIn<PersonnelCheckIn>(data, { outbox: safetyOutboxService });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [activeTenant?.id, ...KEYS.all] });
      toast.success('Check-in recorded');
    },
    onError: (err: Error) => {
      toast.error('Failed to record check-in', parseApiError(err).message);
    },
  });
}

/**
 * Trigger an SOS / emergency alert
 */
export function useTriggerSOS() {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantStore();

  return useMutation({
    mutationFn: async (data: {
      session_id?: string;
      lat?: number;
      lng?: number;
      message?: string;
    }) => {
      // Lane-2 field endpoint (P1-12): one event id for this alarm, reused on
      // every retry so the server raises it once. Legacy route only behind
      // VITE_FIELD_INGEST_LEGACY or against a server without the endpoint.
      // The safety outbox keeps it until the server stores it (S-06 drain).
      return sendSos(data, { outbox: safetyOutboxService });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [activeTenant?.id, ...KEYS.all] });
      // An SOS under an exercise incident is still raised and paged, labelled (D6).
      toast.success(result?.exercise ? 'SOS alert sent (EXERCISE)' : 'SOS alert sent');
    },
    onError: (err: Error) => {
      toast.error('Failed to send SOS alert', parseApiError(err).message);
    },
  });
}

/**
 * Create a safety zone
 */
export function useCreateSafetyZone() {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantStore();

  return useMutation({
    mutationFn: async (data: {
      incident_id?: string;
      name: string;
      zone_type: string;
      geometry_geojson?: Record<string, unknown>;
      proximity_threshold_meters?: number;
      is_active?: boolean;
    }) => {
      return api.post<SafetyZone>('/personnel-tracking/safety-zones', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [activeTenant?.id, ...KEYS.all] });
      toast.success('Safety zone created');
    },
    onError: (err: Error) => {
      toast.error('Failed to create safety zone', parseApiError(err).message);
    },
  });
}

/**
 * Update an existing safety zone
 */
export function useUpdateSafetyZone() {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantStore();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: {
        name?: string;
        zone_type?: string;
        geometry_geojson?: Record<string, unknown>;
        proximity_threshold_meters?: number;
        is_active?: boolean;
        zone_policy?: SafetyZonePolicy;
      };
    }) => {
      return api.patch<SafetyZone>(`/personnel-tracking/safety-zones/${id}`, data);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [activeTenant?.id, ...KEYS.all] });
      queryClient.invalidateQueries({ queryKey: [activeTenant?.id, ...KEYS.safetyZone(id)] });
      toast.success('Safety zone updated');
    },
    onError: (err: Error) => {
      toast.error('Failed to update safety zone', parseApiError(err).message);
    },
  });
}

/**
 * Delete a safety zone
 */
export function useDeleteSafetyZone() {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantStore();

  return useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/personnel-tracking/safety-zones/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [activeTenant?.id, ...KEYS.all] });
      toast.success('Safety zone deleted');
    },
    onError: (err: Error) => {
      toast.error('Failed to delete safety zone', parseApiError(err).message);
    },
  });
}

/**
 * Acknowledge a safety zone alert
 */
export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantStore();

  return useMutation({
    mutationFn: async (alertId: string) => {
      return api.post<SafetyZoneAlert>(
        `/personnel-tracking/safety-zones/alerts/${alertId}/acknowledge`,
        {}
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [activeTenant?.id, ...KEYS.all] });
      toast.success('Alert acknowledged');
    },
    onError: (err: Error) => {
      toast.error('Failed to acknowledge alert', parseApiError(err).message);
    },
  });
}

// ---------------------------------------------------------------------------
// Real-time WebSocket hook (piggybacks on existing /map socket)
// ---------------------------------------------------------------------------

/**
 * Content-free position-change notice (server -> personnel room, P0-13): new
 * points were stored; refetch GET /personnel-tracking/positions/latest.
 */
export interface PersonnelPositionUpdateEvent {
  type?: 'personnel:position-update';
  tenantId?: string;
  timestamp?: string;
}

export interface PersonnelSafetyAlertEvent {
  alert_id: string;
  safety_zone_id: string;
  zone_name?: string;
  session_id: string;
  user_id: string;
  user_name?: string;
  alert_type: 'entered' | 'exited' | 'proximity' | 'breach';
  lat: number;
  lng: number;
  distance_meters?: number;
  timestamp: string;
}

export interface PersonnelSOSAlertEvent {
  session_id: string;
  user_id: string;
  user_name?: string;
  lat?: number;
  lng?: number;
  message?: string;
  timestamp: string;
  /** Emergency check-in id — the SOS identity the polling fallback also returns. */
  event_id?: string;
  incident_id?: string;
  incident_name?: string;
  /** Session is under a drill incident; the console alarm labels it EXERCISE. */
  is_exercise?: boolean;
}

export interface UsePersonnelSocketOptions {
  /** Existing socket ref from useMapSocket — avoids creating a duplicate /map connection. */
  socketRef: MutableRefObject<Socket | null>;
  /** Whether the socket is currently connected. */
  isConnected: boolean;
  enabled?: boolean;
}

/**
 * Hook for real-time personnel tracking updates via the /map socket namespace.
 *
 * Registers personnel event listeners on an existing /map socket (provided by
 * the caller, typically from useMapSocket) instead of creating its own
 * connection.  This prevents duplicate /map socket connections.
 *
 * Joins the server's personnel room (`join:personnel`), which the server
 * admits only with personnel_tracking:view (P0-13 purpose-scoped rooms; the
 * tenant-wide map room never carries positions), and leaves it on cleanup
 * unless another user of the same socket (the app-shell SOS alarm) still holds
 * it (claimPersonnelRoom).
 * The join is repeated on every (re)connect because this effect re-runs when
 * `isConnected` flips; the server may also evict the socket ('room:evicted')
 * when the permission is revoked.
 *
 * Listens for:
 * - `personnel:position-update`  -- content-free notice; invalidates latest positions cache
 * - `personnel:safety-alert`     -- shows toast + invalidates safety zone alerts cache
 * - `personnel:sos-alert`        -- invalidates personnel caches only. The SOS
 *   itself is raised by the app-shell SosAlarmListener (persistent modal +
 *   audible alarm on every console page), NOT here: this hook only runs on the
 *   Command and Safety dashboards, and a 5-second toast is not an alarm.
 */
export function usePersonnelSocket(options: UsePersonnelSocketOptions): void {
  const { socketRef, isConnected, enabled = true } = options;

  const queryClient = useQueryClient();
  const { activeTenant } = useTenantStore();

  // Store queryClient in a ref so event handlers always have the latest instance
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  const tenantIdRef = useRef(activeTenant?.id);
  tenantIdRef.current = activeTenant?.id;

  useEffect(() => {
    if (!enabled || !isConnected || !socketRef.current) return;

    const socket = socketRef.current;

    const handlePositionUpdate = (_event: PersonnelPositionUpdateEvent) => {
      queryClientRef.current.invalidateQueries({
        queryKey: [tenantIdRef.current, ...KEYS.all, 'positions', 'latest'],
      });
    };

    const handleSafetyAlert = (event: PersonnelSafetyAlertEvent) => {
      const userName = event.user_name || 'Unknown user';
      const zoneName = event.zone_name || 'a safety zone';
      toast.warning(
        'Safety Zone Alert',
        `${userName} ${event.alert_type} ${zoneName}`
      );

      queryClientRef.current.invalidateQueries({
        queryKey: [tenantIdRef.current, ...KEYS.all, 'safety-zones', 'alerts'],
      });
    };

    const handleSOSAlert = (_event: PersonnelSOSAlertEvent) => {
      // No toast: the app-shell SOS alarm owns alerting (see the doc comment).
      // Invalidate sessions and positions so the UI reflects emergency status.
      queryClientRef.current.invalidateQueries({ queryKey: [tenantIdRef.current, ...KEYS.all] });
    };

    socket.on('personnel:position-update', handlePositionUpdate);
    socket.on('personnel:safety-alert', handleSafetyAlert);
    socket.on('personnel:sos-alert', handleSOSAlert);
    // A CLAIM, not a bare join/leave: the app-shell SOS alarm shares this
    // socket (one cached Socket per namespace), so leaving the dashboard must
    // not take the console's alarm out of the personnel room (personnelRoomClaims.ts).
    const releaseRoom = claimPersonnelRoom(socket);

    return () => {
      socket.off('personnel:position-update', handlePositionUpdate);
      socket.off('personnel:safety-alert', handleSafetyAlert);
      socket.off('personnel:sos-alert', handleSOSAlert);
      releaseRoom();
    };
  }, [enabled, isConnected, socketRef]);
}
